import { useLingui } from "@lingui/react/macro";

/**
 * Locale-aware money (P17): `12,50 €` in ro, `€12.50` in en, `12,50 лв.`…
 * The one formatter for prices, balances and fees — never `${cur} ${n}` or
 * `n.toFixed(2)` by hand. An invalid or missing currency code falls back to
 * a plain localized number followed by the code, so a bad payload never
 * crashes a screen.
 */
export function formatMoney(
  amount: number,
  currency: string | null | undefined,
  locale: string,
  options: { maximumFractionDigits?: number; minimumFractionDigits?: number } = {},
): string {
  const code = (currency ?? "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: code,
        ...options,
      }).format(amount);
    } catch {
      // Unknown ISO code in this runtime — fall through to the plain form.
    }
  }
  const plain = new Intl.NumberFormat(locale, {
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
    minimumFractionDigits: options.minimumFractionDigits,
  }).format(amount);
  return code ? `${plain} ${code}` : plain;
}

/** `formatMoney` bound to the active UI locale. */
export function useFormatMoney() {
  const { i18n } = useLingui();
  return (
    amount: number,
    currency: string | null | undefined,
    options?: { maximumFractionDigits?: number; minimumFractionDigits?: number },
  ) => formatMoney(amount, currency, i18n.locale, options);
}
